import type { SharedTableName } from '@app/icare/db/types'
import { msg } from '@lingui/core/macro'

/**
 * Finding configuration types
 */
type FindingConfigBase = {
  id: string
  group: string
  message: ReturnType<typeof msg>
}

export type PatientFieldFindingConfig = FindingConfigBase & {
  type: 'patient-field'
  fieldPath: string
  jsonColumn: string
  jsonKey: string
}

export type CollectionMissingFindingConfig = FindingConfigBase & {
  type: 'collection-missing'
  relationTable: SharedTableName
}

export type RelationFieldFindingConfig = FindingConfigBase & {
  type: 'relation-field'
  relationTable: SharedTableName
  fieldName: string
}

export type CustomFindingConfig = FindingConfigBase & {
  type: 'custom'
}

export type FindingConfig =
  | PatientFieldFindingConfig
  | CollectionMissingFindingConfig
  | RelationFieldFindingConfig
  | CustomFindingConfig

/**
 * Single source of truth for all patient findings
 * Key = ID (same value)
 */
export const PATIENT_FINDINGS_CONFIG = {
  // Personal findings
  PersonalFirstNameRequired: {
    type: 'patient-field',
    id: 'PersonalFirstNameRequired',
    group: 'personal',
    message: msg`Vorname fehlt`,
    fieldPath: 'personal.firstName',
    jsonColumn: 'patient.personal',
    jsonKey: 'firstName',
  },

  PersonalBirthdateRequired: {
    type: 'patient-field',
    id: 'PersonalBirthdateRequired',
    group: 'personal',
    message: msg`Geburtsdatum fehlt`,
    fieldPath: 'personal.birthdate',
    jsonColumn: 'patient.personal',
    jsonKey: 'birthdate',
  },

  // Address findings
  AddressIncomplete: {
    type: 'custom',
    id: 'AddressIncomplete',
    group: 'address',
    message: msg`Adresse ist unvollständig. Straße, PLZ und Ort müssen ausgefüllt werden.`,
  },

  // Patient address (delivery/billing) findings
  AddressesDeliveryIncomplete: {
    type: 'custom',
    id: 'AddressesDeliveryIncomplete',
    group: 'addresses',
    message: msg`Lieferadresse ist unvollständig. Nachname, Straße, PLZ und Ort müssen ausgefüllt werden.`,
  },

  AddressesBillingIncomplete: {
    type: 'custom',
    id: 'AddressesBillingIncomplete',
    group: 'addresses',
    message: msg`Rechnungsadresse ist unvollständig. Nachname, Straße, PLZ und Ort müssen ausgefüllt werden.`,
  },

  // Collection missing findings
  RelativesMissing: {
    type: 'collection-missing',
    id: 'RelativesMissing',
    group: 'relatives',
    message: msg`Es ist kein Angehöriger hinterlegt.`,
    relationTable: 'patientRelative',
  },

  RelativesAddressIncomplete: {
    type: 'custom',
    id: 'RelativesAddressIncomplete',
    group: 'relatives',
    message: msg`Angehörigen-Adresse ist unvollständig. Straße, PLZ und Ort müssen ausgefüllt werden.`,
  },

  RelativesFirstNameRequired: {
    type: 'relation-field',
    id: 'RelativesFirstNameRequired',
    group: 'relatives',
    message: msg`Vorname von Angehörigen ist ein Pflichtfeld und muss ausgefüllt werden.`,
    relationTable: 'patientRelative',
    fieldName: 'firstName',
  },

  RelativesLastNameRequired: {
    type: 'relation-field',
    id: 'RelativesLastNameRequired',
    group: 'relatives',
    message: msg`Nachname von Angehörigen ist ein Pflichtfeld und muss ausgefüllt werden.`,
    relationTable: 'patientRelative',
    fieldName: 'lastName',
  },

  RelativesContactRequired: {
    type: 'relation-field',
    id: 'RelativesContactRequired',
    group: 'relatives',
    message: msg`Kontaktinformation (Telefon oder Mobil) von Angehörigen ist erforderlich.`,
    relationTable: 'patientRelative',
    fieldName: 'contact',
  },

  CostCarriersMissing: {
    type: 'collection-missing',
    id: 'CostCarriersMissing',
    group: 'costCarriers',
    message: msg`Es ist kein Kostenträger hinterlegt.`,
    relationTable: 'patientCostCarrier',
  },

  AdTeamsMissing: {
    type: 'collection-missing',
    id: 'AdTeamsMissing',
    group: 'adTeams',
    message: msg`Es ist kein AD-Team hinterlegt.`,
    relationTable: 'patientAdTeam',
  },

  CareManagersMissing: {
    type: 'collection-missing',
    id: 'CareManagersMissing',
    group: 'careManagers',
    message: msg`Es ist kein Versorgungsmanager hinterlegt.`,
    relationTable: 'patientCareManager',
  },

  ApplicantsMissing: {
    type: 'collection-missing',
    id: 'ApplicantsMissing',
    group: 'applicants',
    message: msg`Es ist kein Anmelder hinterlegt.`,
    relationTable: 'patientApplicant',
  },

  DoctorsMissing: {
    type: 'collection-missing',
    id: 'DoctorsMissing',
    group: 'doctors',
    message: msg`Es ist kein Arzt hinterlegt.`,
    relationTable: 'patientDoctor',
  },

  DoctorsNoPrescriber: {
    type: 'custom',
    id: 'DoctorsNoPrescriber',
    group: 'doctors',
    message: msg`Es ist kein Arzt als Rezeptierer hinterlegt.`,
  },

  // Per-relation field findings
  CostCarriersIkNumberRequired: {
    type: 'relation-field',
    id: 'CostCarriersIkNumberRequired',
    group: 'costCarriers',
    message: msg`IK-Nummer ist ein Pflichtfeld und muss ausgefüllt werden.`,
    relationTable: 'patientCostCarrier',
    fieldName: 'ikNumber',
  },

  CostCarriersServiceNumberRequired: {
    type: 'relation-field',
    id: 'CostCarriersServiceNumberRequired',
    group: 'costCarriers',
    message: msg`Versichertennummer ist ein Pflichtfeld und muss ausgefüllt werden.`,
    relationTable: 'patientCostCarrier',
    fieldName: 'serviceNumber',
  },

  CostCarriersExemptionRequired: {
    type: 'relation-field',
    id: 'CostCarriersExemptionRequired',
    group: 'costCarriers',
    message: msg`Befreiung ist erforderlich.`,
    relationTable: 'patientCostCarrier',
    fieldName: 'excemption',
  },

  // Anamnesis findings
  AnamnesisDiagnosisRequired: {
    type: 'custom',
    id: 'AnamnesisDiagnosisRequired',
    group: 'anamnesis',
    message: msg`Mindestens eine Diagnose muss hinterlegt werden.`,
  },

  AnamnesisCareLevelRequired: {
    type: 'custom',
    id: 'AnamnesisCareLevelRequired',
    group: 'anamnesis',
    message: msg`Mindestens ein aktiver Pflegegrad muss hinterlegt werden.`,
  },

  // Care service findings
  CareServicesMissing: {
    type: 'custom',
    id: 'CareServicesMissing',
    group: 'careServices',
    message: msg`Es ist kein Pflegedienst hinterlegt.`,
  },

  CareServiceNotNeededReasonRequired: {
    type: 'custom',
    id: 'CareServiceNotNeededReasonRequired',
    group: 'careServices',
    message: msg`Ein Grund für "Pflegedienst nicht benötigt" muss angegeben werden.`,
  },

  // Deceased finding
  DeceasedDateRequired: {
    type: 'custom',
    id: 'DeceasedDateRequired',
    group: 'deceased',
    message: msg`Sterbedatum oder ungefährer Zeitraum muss angegeben werden.`,
  },

  // Forms findings
  FormsPatientInformationRequired: {
    type: 'custom',
    id: 'FormsPatientInformationRequired',
    group: 'forms',
    message: msg`Patienteninformation Formular ist erforderlich.`,
  },

  FormsPatientMasterDataSheetRequired: {
    type: 'custom',
    id: 'FormsPatientMasterDataSheetRequired',
    group: 'forms',
    message: msg`Stammblatt Formular ist erforderlich.`,
  },

  FormsPatientConsentAndConfidentialityRequired: {
    type: 'custom',
    id: 'FormsPatientConsentAndConfidentialityRequired',
    group: 'forms',
    message: msg`Einwilligungs- und Schweigepflichtserklärung ist erforderlich.`,
  },

  FormsPatientSupplyRequired: {
    type: 'custom',
    id: 'FormsPatientSupplyRequired',
    group: 'forms',
    message: msg`Versorgungsauftrag ist erforderlich.`,
  },

  // Therapy breaks findings
  TherapyBreaksRangeInvalid: {
    type: 'custom',
    id: 'TherapyBreaksRangeInvalid',
    group: 'therapies',
    message: msg`Therapiepause darf maximal 28 Tage dauern.`,
  },

  TherapyBreaksRangeRequired: {
    type: 'custom',
    id: 'TherapyBreaksRangeRequired',
    group: 'therapies',
    message: msg`Therapiepause Start- und Enddatum sind erforderlich.`,
  },

  TherapyBreaksTypeRequired: {
    type: 'custom',
    id: 'TherapyBreaksTypeRequired',
    group: 'therapies',
    message: msg`Therapiepause Typ ist erforderlich.`,
  },

  // Separate residence findings (consolidated by address type)
  SeparateResidencesMainAddressIncomplete: {
    type: 'custom',
    id: 'SeparateResidencesMainAddressIncomplete',
    group: 'separateResidences',
    message: msg`Urlaubs-/Ferienaufenthalt Adresse ist unvollständig. Vorname, Nachname, Straße, PLZ und Ort müssen ausgefüllt werden.`,
  },

  SeparateResidencesDeliveryAddressIncomplete: {
    type: 'custom',
    id: 'SeparateResidencesDeliveryAddressIncomplete',
    group: 'separateResidences',
    message: msg`Gesonderter Aufenthaltsort Lieferadresse ist unvollständig. Vorname, Nachname, Straße, PLZ und Ort müssen ausgefüllt werden.`,
  },

  SeparateResidencesBillingAddressIncomplete: {
    type: 'custom',
    id: 'SeparateResidencesBillingAddressIncomplete',
    group: 'separateResidences',
    message: msg`Gesonderter Aufenthaltsort Rechnungsadresse ist unvollständig. Vorname, Nachname, Straße, PLZ und Ort müssen ausgefüllt werden.`,
  },

  SeparateResidencesVacationDeliveryAddressIncomplete: {
    type: 'custom',
    id: 'SeparateResidencesVacationDeliveryAddressIncomplete',
    group: 'separateResidences',
    message: msg`Urlaubs-/Ferienaufenthalt Lieferadresse ist unvollständig. Vorname, Nachname, Straße, PLZ und Ort müssen ausgefüllt werden.`,
  },

  SeparateResidencesVacationBillingAddressIncomplete: {
    type: 'custom',
    id: 'SeparateResidencesVacationBillingAddressIncomplete',
    group: 'separateResidences',
    message: msg`Urlaubs-/Ferienaufenthalt Rechnungsadresse ist unvollständig. Vorname, Nachname, Straße, PLZ und Ort müssen ausgefüllt werden.`,
  },

  // Custom findings (complex logic)
  PharmacyMissing: {
    type: 'custom',
    id: 'PharmacyMissing',
    group: 'pharmacies',
    message: msg`Es ist keine Apotheke hinterlegt.`,
  },

  ClinicsMissing: {
    type: 'custom',
    id: 'ClinicsMissing',
    group: 'clinics',
    message: msg`Es ist keine Klinik hinterlegt.`,
  },

  ClinicsNoPrescriber: {
    type: 'custom',
    id: 'ClinicsNoPrescriber',
    group: 'clinics',
    message: msg`Es ist keine Klinik als Rezeptierer hinterlegt.`,
  },
} as const satisfies Record<string, FindingConfig>

export type PatientFindingsConfigRegistry = typeof PATIENT_FINDINGS_CONFIG
export type PatientFindingConfigKey = keyof PatientFindingsConfigRegistry
